import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "../../services/api";
import { Button, PageHeader, SectionPanel, StatusBadge } from "../../components/ui";
import {
  canAccessTenantAdmin,
  getPermissionSet,
  getRoleNames,
  hasAnyPermission,
} from "../../utils/tenantAdminAccess";
import { useUser } from "../../context/UserContext";
import {
  TENANT_ADMIN_WORKFLOWS,
  getWorkflowDetailPath,
} from "./workflowCatalog";
import { getIntegrationDetailPath } from "./integrationCatalog";
import { STATUS_META, getStatusLabel, resolveHubTileStatus } from "./tenantAdminStatus";
import {
  buildTenantHealthReport,
  resolveOnboardingStage,
  TENANT_ONBOARDING_STAGE_TYPES,
} from "./tenantHealthEngine";
import { fetchGovernanceStructure, findGovernanceLayer } from "../../services/governanceService";
import { TENANT_GOVERNANCE_PERMISSION_GROUPS } from "../../constants/tenantGovernancePermissions";

function canViewTile(tile, user) {
  const roleNames = new Set(getRoleNames(user));
  if (roleNames.has("tenantadmin") || roleNames.has("platformadmin")) {
    return true;
  }

  if (!roleNames.has("projectleider")) {
    return false;
  }

  if (!Array.isArray(tile.permissionsAny) || tile.permissionsAny.length === 0) {
    return true;
  }

  return hasAnyPermission(user, tile.permissionsAny);
}

const TILE_SECTIONS = [
  {
    key: "algemeen",
    title: "Bedrijfsinrichting",
    tiles: [
      {
        key: "users",
        title: "Gebruikers",
        description: "Beheer accounts, toegang en basisrollen voor gebruikers binnen de tenant.",
        to: "/tenant-admin/users",
        permissionsAny: TENANT_GOVERNANCE_PERMISSION_GROUPS.usersManage,
      },
      {
        key: "roles",
        title: "Rollen & rechten",
        description: "Beheer rolstructuur en permissies voor tenant-brede toegang.",
        to: "/tenant-admin/roles",
        permissionsAny: TENANT_GOVERNANCE_PERMISSION_GROUPS.rolesManage,
      },
      {
        key: "settings",
        title: "Bedrijfsinstellingen",
        description: "Algemene tenantinstellingen, policies en basisconfiguratie.",
        to: "/tenant-admin/settings",
        permissionsAny: TENANT_GOVERNANCE_PERMISSION_GROUPS.settingsView,
      },
      {
        key: "branding_documents",
        title: "Huisstijl & Documenten",
        description:
          "Beheer logo, kleuren, documentindeling en standaardinstellingen voor werkbonnen, mandagstaten en rapportages.",
        to: "/tenant-admin/bedrijfsinrichting/huisstijl-documenten",
        permissionsAny: TENANT_GOVERNANCE_PERMISSION_GROUPS.companyDetailsView,
      },
      {
        key: "document_library",
        title: "Documentbibliotheek",
        description:
          "Beheer centrale documenten, templates en bijlagen voor werkbonnen, mandagstaten, inspecties en rapportages.",
        to: "/tenant-admin/bedrijfsinrichting/documenten",
        permissionsAny: TENANT_GOVERNANCE_PERMISSION_GROUPS.documentsView,
      },
      {
        key: "modules",
        title: "Modules",
        description: "Zet tenantmodules aan of uit en beheer modulegedrag.",
        to: "/tenant-admin/modules",
        permissionsAny: TENANT_GOVERNANCE_PERMISSION_GROUPS.modulesView,
      },
      {
        key: "tenant_status",
        title: "Inrichtingsstatus & monitoring",
        description: "Operationeel overzicht van gezondheid, inrichting en activiteitssignalen.",
        to: "/tenant-admin/status",
        permissionsAny: TENANT_GOVERNANCE_PERMISSION_GROUPS.statusView,
      },
    ],
  },
  {
    key: "workflows",
    title: "Werkprocessen",
    tiles: TENANT_ADMIN_WORKFLOWS.map((workflow) => ({
      key: workflow.key,
      title: workflow.title,
      description: workflow.description,
      to: getWorkflowDetailPath(workflow.slug),
      permissionsAny: workflow.permissionsAny,
    })),
  },
  {
    key: "integraties",
    title: "Integraties",
    tiles: [
      {
        key: "integration_moneybird",
        title: "Moneybird",
        description: "Beheer Moneybird koppeling, syncgedrag en verbindingsstatus.",
        to: getIntegrationDetailPath("moneybird"),
        permissionsAny: TENANT_GOVERNANCE_PERMISSION_GROUPS.integrationsView,
      },
      {
        key: "integration_tu",
        title: "Technische Unie / OCI",
        description: "Configureer OCI/TU koppeling voor inkoop en artikeldata.",
        to: getIntegrationDetailPath("technische-unie-oci"),
        permissionsAny: TENANT_GOVERNANCE_PERMISSION_GROUPS.integrationsView,
      },
      {
        key: "integration_mail",
        title: "Mailinstellingen",
        description: "Stel mailgerelateerde tenantinstellingen en deliverability in.",
        to: getIntegrationDetailPath("mailinstellingen"),
        permissionsAny: TENANT_GOVERNANCE_PERMISSION_GROUPS.integrationsView,
      },
      {
        key: "pdf_templates",
        title: "PDF templates",
        description: "Beheer outputtemplates voor rapportages en documenten.",
        to: getIntegrationDetailPath("pdf-templates"),
        permissionsAny: TENANT_GOVERNANCE_PERMISSION_GROUPS.integrationsView,
      },
      {
        key: "branding",
        title: "Huisstijl / branding",
        description: "Beheer tenant-eigen uitstraling voor documenten en portalen.",
        to: getIntegrationDetailPath("huisstijl-branding"),
        permissionsAny: TENANT_GOVERNANCE_PERMISSION_GROUPS.integrationsView,
      },
    ],
  },
];

function TileCard({ tile, status }) {
  const meta = STATUS_META[status] || STATUS_META.concept;
  const tone =
    status === "healthy" || status === "complete"
      ? "success"
      : status === "attention" || status === "in_progress"
        ? "warning"
        : status === "critical" || status === "blocked"
          ? "danger"
          : "neutral";

  return (
    <article className="border border-slate-200 bg-white p-4 transition hover:bg-slate-50">
      <div className="mb-3 flex items-start justify-between gap-3">
        <h3 className="text-base font-semibold text-slate-900">{tile.title}</h3>
        <StatusBadge tone={tone} label={getStatusLabel(status)} />
      </div>
      <p className="mb-4 text-sm text-slate-600">{tile.description}</p>
      <Button as={Link} to={tile.to} tone="secondary" size="sm">
        {meta.cta}
      </Button>
    </article>
  );
}

export default function TenantAdminHubPage() {
  const { user } = useUser();
  const [settings, setSettings] = useState(null);
  const [users, setUsers] = useState([]);
  const [governance, setGovernance] = useState(null);
  const [insights, setInsights] = useState({ usersCount: 0, roleCount: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const roleNames = useMemo(() => new Set(getRoleNames(user)), [user]);
  const permissionSet = useMemo(() => getPermissionSet(user), [user]);

  useEffect(() => {
    let alive = true;

    function resolveUsersCount(payload) {
      if (Array.isArray(payload)) return payload.length;
      if (Array.isArray(payload?.users)) return payload.users.length;
      if (Array.isArray(payload?.items)) return payload.items.length;
      return 0;
    }

    function resolveUsersList(payload) {
      if (Array.isArray(payload)) return payload;
      if (Array.isArray(payload?.users)) return payload.users;
      if (Array.isArray(payload?.items)) return payload.items;
      return [];
    }

    async function loadHubData() {
      setLoading(true);
      setError("");
      try {
        const [settingsRes, usersRes, rbacRes, governanceRes] = await Promise.allSettled([
          api.get("/tenant/settings"),
          api.get("/users"),
          api.get("/rbac/configurator"),
          fetchGovernanceStructure(),
        ]);
        if (!alive) return;

        if (settingsRes.status === "fulfilled") {
          setSettings(settingsRes.value?.data || null);
        } else {
          setSettings(null);
        }

        const usersCount =
          usersRes.status === "fulfilled" ? resolveUsersCount(usersRes.value?.data) : 0;
        const usersList =
          usersRes.status === "fulfilled" ? resolveUsersList(usersRes.value?.data) : [];
        const roleCount =
          rbacRes.status === "fulfilled" && Array.isArray(rbacRes.value?.data?.roles)
            ? rbacRes.value.data.roles.length
            : 0;
        const governancePayload =
          governanceRes.status === "fulfilled" ? governanceRes.value : null;
        setGovernance(governancePayload);
        setUsers(usersList);
        setInsights({ usersCount, roleCount });

        if (settingsRes.status !== "fulfilled") {
          const err = settingsRes.reason;
          setError(err?.response?.data?.message || "Tenant settings konden niet worden geladen.");
        }
      } catch (err) {
        if (!alive) return;
        setSettings(null);
        setUsers([]);
        setGovernance(null);
        setInsights({ usersCount: 0, roleCount: 0 });
        setError(err?.response?.data?.message || "Tenant settings konden niet worden geladen.");
      } finally {
        if (alive) setLoading(false);
      }
    }

    if (canAccessTenantAdmin(user)) {
      loadHubData();
    } else {
      setLoading(false);
    }

    return () => {
      alive = false;
    };
  }, [user]);

  const visibleSections = useMemo(() => {
    const tenantLayer = findGovernanceLayer(governance, "tenant");
    const governanceSectionLabels = new Map(
      (Array.isArray(tenantLayer?.sections) ? tenantLayer.sections : []).map((item) => [
        String(item?.key || ""),
        String(item?.label || ""),
      ])
    );
    const tenantLabel = String(tenantLayer?.label || "Bedrijfsinrichting").trim();
    const workflowsLabel = governanceSectionLabels.get("workflows") || "Werkprocessen";
    const integrationsLabel = governanceSectionLabels.get("tenant_integrations") || "Integraties";

    return TILE_SECTIONS.map((section) => ({
      ...section,
      title:
        section.key === "algemeen"
          ? tenantLabel
          : section.key === "workflows"
            ? workflowsLabel
            : section.key === "integraties"
              ? integrationsLabel
              : section.title,
      tiles: section.tiles.filter((tile) => canViewTile(tile, user)),
    })).filter((section) => section.tiles.length > 0);
  }, [governance, user]);

  const onboardingReport = useMemo(
    () =>
      buildTenantHealthReport({
        settings,
        users,
        workorders: [],
        photos: { items: [] },
        supportTickets: { items: [] },
        apiErrors: [],
        mailErrors: [],
        warnings: [],
      }),
    [settings, users]
  );

  const onboardingStage = resolveOnboardingStage(onboardingReport.readinessScore);
  const onboardingMeta =
    TENANT_ONBOARDING_STAGE_TYPES[onboardingStage] || TENANT_ONBOARDING_STAGE_TYPES.inrichting_nodig;
  const openAttentionPoints = onboardingReport.sections.configuration.filter(
    (item) => item.status !== "healthy"
  ).length;
  const canViewStatusPage = canViewTile(
    {
      permissionsAny: TENANT_GOVERNANCE_PERMISSION_GROUPS.statusView,
    },
    user
  );

  return (
    <div className="min-h-screen bg-slate-50 p-4 md:p-6">
      <div className="mx-auto max-w-6xl space-y-4">
        {!loading ? (
          <SectionPanel title="Inrichtingsvoortgang" description={`Je omgeving is voor ${onboardingReport.readinessScore}% ingericht.`}>
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <p className="mt-1 text-sm text-slate-600">
                  {openAttentionPoints} open aandachtspunt(en) voor inrichting.
                </p>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <StatusBadge
                  tone={openAttentionPoints > 0 ? "warning" : "success"}
                  label={onboardingMeta.label}
                />
                {canViewStatusPage ? (
                  <Button as={Link} to="/tenant-admin/status" tone="secondary" size="sm">
                    Bekijk status
                  </Button>
                ) : null}
              </div>
            </div>
            <div className="mt-3 h-2 w-full overflow-hidden bg-slate-200">
              <div
                className={`h-2 ${onboardingMeta.progressClass}`}
                style={{ width: `${onboardingReport.readinessScore}%` }}
              />
            </div>
          </SectionPanel>
        ) : null}

        <PageHeader
          eyebrow="Beheerhub"
          title="Bedrijfsinrichting"
          description="Centrale beheerhub voor organisatie-inrichting, werkprocessen en integraties zonder operationele uitvoering te mengen."
          meta={
            <>
              <StatusBadge tone="neutral" label="Beheerstructuur" />
              <StatusBadge tone="neutral" label={`Rollen: ${Array.from(roleNames).join(", ") || "-"}`} />
              <StatusBadge tone="neutral" label={`Rechten geladen: ${permissionSet.size}`} />
              <StatusBadge tone="neutral" label={`Gebruikers: ${insights.usersCount}`} />
            </>
          }
        />

        {loading ? <p className="text-sm text-slate-600">Tenantbeheerstatus laden...</p> : null}
        {!loading && error ? (
          <p className="border border-amber-300 bg-amber-50 p-3 text-sm text-amber-800">
            {error}
          </p>
        ) : null}

        <div className="space-y-7">
          {visibleSections.map((section) => (
            <SectionPanel key={section.key} title={section.title} contentClassName="pt-3">
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
                {section.tiles.map((tile) => (
                  <TileCard
                    key={tile.key}
                    tile={tile}
                    status={resolveHubTileStatus(tile.key, settings, insights)}
                  />
                ))}
              </div>
            </SectionPanel>
          ))}
        </div>
      </div>
    </div>
  );
}
